import { Box, Button, Grid, Typography, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';
import { Link } from 'react-router-dom';

import { useHoveredModerationReportId } from 'components/IncidentsMap/SearchDrawer/hooks/useHoveredModerationReportId';
import { useReportMutations } from 'features/reports/hooks/useReport';
import { Report } from 'features/reports/report.types';
import { getTimeAgo } from 'shared/dates';

type PhotoToModerateProps = {
  geoEventName: string;
  report: Report;
};

const sxLineClamped = {
  display: '-webkit-box',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  WebkitLineClamp: '2',
  WebkitBoxOrient: 'vertical',
};

export const PhotoToModerate = ({
  geoEventName,
  report,
}: PhotoToModerateProps): JSX.Element => {
  const { t } = useTranslation();
  const { setHoveredReportId } = useHoveredModerationReportId();
  const { isReportUpdating, onDeleteReport } = useReportMutations(report.id);

  const handleMouseEnter = (): void => setHoveredReportId(report.id);

  const handleMouseLeave = (): void => setHoveredReportId(null);

  const onReject = async (): Promise<void> => {
    try {
      await onDeleteReport(false);
      enqueueSnackbar(t('moderateSubmission.success'), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(t('moderateSubmission.error'), { variant: 'error' });
    }
  };

  return (
    <Grid
      size={1}
      sx={{ mb: 2 }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Box
        component="img"
        src={report.media[0]?.url}
        alt={t('home.searchDrawer.photoModeration.photoAltText')}
        sx={{ width: '100%', height: 95, objectFit: 'cover' }}
      />
      <Typography
        variant="body2"
        sx={{ fontWeight: 'fontWeightBold', ...sxLineClamped }}
      >
        {geoEventName}
      </Typography>
      <Typography variant="subtitle1" color="secondary">
        {getTimeAgo(report.dateCreated)}
      </Typography>
      <Typography variant="subtitle1" sx={{ mb: 1, mt: 1, ...sxLineClamped }}>
        {report.message}
      </Typography>
      <Stack direction="row" spacing={1} justifyContent="space-between">
        <Button
          color="success"
          component={Link}
          disabled={isReportUpdating}
          to={{
            pathname: `/reports/submission/${report.id}`,
            state: { returnOnSuccess: true },
          }}
          onClick={handleMouseLeave}
          sx={{ fontWeight: 'fontWeightMedium', fontSize: 14 }}
        >
          {t('home.searchDrawer.photoModeration.button.moderate')}
        </Button>
        <Button
          color="error"
          disabled={isReportUpdating}
          onClick={onReject}
          sx={{ fontWeight: 'fontWeightMedium', fontSize: 14 }}
        >
          {t('home.searchDrawer.photoModeration.button.reject')}
        </Button>
      </Stack>
    </Grid>
  );
};
